import React, { useEffect, useState } from "react";
import {
  Avatar, Box, Button, CircularProgress, IconButton, Stack, TextField, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import api from "../../api/client";
import { extractErrorMessage } from "../../utils/errorMessage";
import RichTextView from "../common/RichTextView";

export function replySubject(subject) {
  const s = String(subject || "").trim();
  if (!s) return "Re: (no subject)";
  return /^re:\s/i.test(s) ? s : `Re: ${s}`;
}

function fmtFull(d) {
  return new Date(d).toLocaleString("en-IN", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

function initials(s) {
  if (!s) return "?";
  const parts = String(s).trim().split(/\s+/);
  return (parts[0]?.[0] || "").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
}

function avatarColor(name) {
  const colors = ["#6C50BF", "#39C645", "#F9BC06", "#5C85FF", "#EC4899", "#F97316", "#14B8A6"];
  let sum = 0;
  for (const c of String(name || "")) sum += c.charCodeAt(0);
  return colors[sum % colors.length];
}

function ThreadMessage({ item, myUserId }) {
  const fromMe = String(item.senderId || item.sender?._id || "") === String(myUserId);
  const name = fromMe ? "You" : (item.senderName || "Unknown");
  const color = avatarColor(name === "You" ? "You" : name);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: fromMe ? "primary.light" : "divider",
        bgcolor: fromMe ? "#F4EDFA" : "background.paper",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={1.25}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: color, fontSize: 12, fontWeight: 700 }}>
          {initials(name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{name}</Typography>
            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{fmtFull(item.createdAt)}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.25 }}>
            {fromMe ? `To ${item.receiverName || "recipient"}` : `From ${item.senderName || name}`}
          </Typography>
        </Box>
      </Stack>
      {item.message
        ? <RichTextView html={item.message} sx={{ fontSize: 14, lineHeight: 1.65 }} />
        : <Typography sx={{ fontSize: 14, lineHeight: 1.65, color: "text.primary" }}>(no body)</Typography>}
      {item.files?.length > 0 && (
        <Stack spacing={0.75} mt={1.5}>
          {item.files.map((f, i) => (
            <Stack
              key={i}
              direction="row"
              spacing={1}
              alignItems="center"
              component="a"
              href={f}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 12, color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
            >
              <AttachFileOutlinedIcon sx={{ fontSize: 14 }} />
              <span>{decodeURIComponent(String(f).split("/").pop() || `Attachment ${i + 1}`)}</span>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Shared mail-thread panel: loads messages for one thread (conversationId),
 * shows chronological messages, inline reply (no To/Subject fields).
 */
export default function MessageConversationView({
  conversationId,
  myUserId,
  otherName,
  otherEmail,
  subject,
  seedMessage,
  onSent,
  footerExtra = null,
}) {
  const [thread, setThread] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const color = avatarColor(otherName);

  const loadThread = async () => {
    const threadId = conversationId || seedMessage?.conversationId;
    if (!threadId) {
      setThread(seedMessage ? [seedMessage] : []);
      return;
    }
    setLoadingThread(true);
    try {
      const res = await api.get(`/api/mailmessage/getThread/${threadId}`);
      const list = Array.isArray(res.data?.messages) ? res.data.messages : [];
      setThread(list.length ? list : (seedMessage ? [seedMessage] : []));
    } catch {
      setThread(seedMessage ? [seedMessage] : []);
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    setReplyOpen(false);
    setReplyText("");
    loadThread();
  }, [conversationId, seedMessage?._id]);

  const sendReply = async () => {
    const text = replyText.trim();
    if (!text) return toast.error("Write a reply message");
    if (!otherEmail) return toast.error("Cannot reply — recipient email missing");
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("recipients[]", otherEmail);
      fd.append("subject", replySubject(subject));
      fd.append("message", text);
      const threadId = conversationId || seedMessage?.conversationId;
      if (threadId) fd.append("conversationId", String(threadId));
      await api.post("/api/mailmessage/sendMessage", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Reply sent");
      setReplyText("");
      setReplyOpen(false);
      await loadThread();
      onSent?.();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: "action.hover" }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          Conversation with <strong>{otherName}</strong>
          {otherEmail ? ` · ${otherEmail}` : ""}
        </Typography>
      </Box>

      <Box sx={{ p: 2, flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        {loadingThread ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress size={22} /></Box>
        ) : thread.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "text.secondary", textAlign: "center", py: 4 }}>
            No messages in this conversation yet.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {thread.map((m) => (
              <ThreadMessage key={m._id} item={m} myUserId={myUserId} />
            ))}
          </Stack>
        )}
      </Box>

      <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
        {!replyOpen ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
            {footerExtra}
            <Button variant="contained" startIcon={<ReplyOutlinedIcon />} onClick={() => setReplyOpen(true)}>
              Reply
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 32, height: 32, bgcolor: color, fontSize: 12, fontWeight: 700 }}>
                {initials(otherName)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Reply to {otherName}</Typography>
                <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
                  Subject: {replySubject(subject)}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => { setReplyOpen(false); setReplyText(""); }} aria-label="Cancel reply">
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={4}
              autoFocus
              placeholder="Write your reply…"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              disabled={sending}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => { setReplyOpen(false); setReplyText(""); }} disabled={sending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendOutlinedIcon />}
                onClick={sendReply}
                disabled={sending || !replyText.trim()}
              >
                {sending ? "Sending…" : "Send reply"}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
